import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Phone, Lock, Pill } from '@phosphor-icons/react';

export default function LoginPage() {
  const { login, user, loading: authLoading } = useAuth();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (authLoading) return <div className="min-h-screen flex items-center justify-center" style={{ background: '#F9F9FA' }}><p style={{ color: '#8A94A6' }}>Loading...</p></div>;
  if (user) return <Navigate to={user.role === 'mr' ? '/mr' : '/admin'} replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(phone, password);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Login failed. Check credentials.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" style={{ background: '#F9F9FA' }}>
      {/* Left: Login Form */}
      <div className="flex-1 flex items-center justify-center px-8">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: '#0A2540' }}>
              <Pill size={22} weight="duotone" color="white" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540' }}>
              PharmaPro
            </h1>
          </div>

          <h2 className="text-3xl font-extrabold tracking-tighter mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540' }}>
            Sign in
          </h2>
          <p className="text-sm mb-8" style={{ color: '#8A94A6', fontFamily: "'IBM Plex Sans', sans-serif" }}>
            Enter your phone number and password to access the system
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div data-testid="login-error" className="p-3 rounded-md text-sm font-medium" style={{ background: '#FFF0F0', color: '#FF3B30', border: '1px solid #FFD4D4' }}>
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Phone Number</Label>
              <div className="relative">
                <Phone size={18} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#8A94A6' }} />
                <Input
                  data-testid="login-phone-input"
                  type="text"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Enter phone number"
                  className="pl-10 h-11"
                  style={{ borderColor: '#E2E4E9' }}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Password</Label>
              <div className="relative">
                <Lock size={18} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#8A94A6' }} />
                <Input
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="pl-10 h-11"
                  style={{ borderColor: '#E2E4E9' }}
                  required
                />
              </div>
            </div>

            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-11 text-sm font-semibold tracking-wide rounded-md transition-colors"
              style={{ background: '#0A2540', color: 'white' }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 p-4 rounded-md" style={{ background: '#F0F4F8', border: '1px solid #E2E4E9' }}>
            <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: '#8A94A6' }}>Demo Credentials</p>
            <p className="text-xs" style={{ color: '#0A2540', fontFamily: "'IBM Plex Sans', sans-serif" }}>
              <strong>Admin:</strong> 9255227867 / 9255227867
            </p>
            <p className="text-xs" style={{ color: '#0A2540', fontFamily: "'IBM Plex Sans', sans-serif" }}>
              <strong>MR:</strong> 9111111111 / 123
            </p>
          </div>
        </div>
      </div>

      {/* Right: Image */}
      <div className="hidden lg:flex flex-1 items-center justify-center relative overflow-hidden" style={{ background: '#0A2540' }}>
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1754321159253-8c9bd02cfb4c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxwaGFybWFjeSUyMHN0b3JlJTIwbW9kZXJuJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzg0MjE4OTUyfDA&ixlib=rb-4.1.0&q=85"
            alt="Pharmacy"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 text-center px-12">
          <h2 className="text-4xl font-extrabold tracking-tighter text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Complete Pharmacy Management
          </h2>
          <p className="text-lg text-white/70" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            Billing, Inventory, CRM, MR Tracking — all in one platform
          </p>
        </div>
      </div>
    </div>
  );
}
